// bluaIQ — Dashboard Component
// Main view: KPIs + Lifecycle + Gantt preview + Alerts

import { apiFetch } from '../utils/api'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle, TrendingUp, DollarSign, Clock, Shield, FileText, Lock, ArrowRight, Upload, X,
  // Phase 1 Dashboard overview pass additions:
  //   - Briefcase: ProjectSummaryCard header icon
  //   - Hammer, ScrollText: QuickActionsCard lucide replacements (emoji → lucide for consistency)
  //   - Wallet: BudgetSnapshotCard header icon
  Briefcase, Hammer, ScrollText, Wallet,
} from 'lucide-react'
import { DEMO_DASHBOARD, DEMO_LIFECYCLE, isDemoProject } from '../utils/demoData'
// Financial Trust P0 (PR-F1): safe money/percent/count display — never render
// NaN/Infinity/null/malformed values as credible money.
import { formatMoney, formatMoneyValue, formatPercentRatio, formatCount, moneyCopy } from '../utils/money'
import { useTakeoffAvailable } from '../takeoff-diagnostic-v3/TakeoffAvailabilityContext'

// ── TYPES ─────────────────────────────────────────

interface Project {
  id: string
  name: string
  address?: string
  city?: string
  state?: string
  type: string
  sqftTarget?: number
  status: string
  phase: number
  startDate?: string
}

interface DashboardData {
  project: Project
  kpis: {
    totalCost: number
    committed: number
    committedPct: number
    progress: number
    pendingBids: number
    expiringInsurance: number
    // Phase 1: the backend at src/server/index.ts:8002 already returns
    // `totalSpent` (sum of receipt totals). It was previously dropped
    // by the FE type. Adding it as optional so the Budget Snapshot
    // can read real spent dollars without any BE change.
    totalSpent?: number
    // PR-F3A: anomalous contract exclusion metadata.
    committedExcludedCount?: number
    committedExcludedAmount?: number
  }
  alerts: Alert[]
  // Phase 2B — additive consumption of the dashboardSummary block the
  // backend started returning in PR #161. Optional + every field
  // optional below so an older BE bundle (or a deploy where the BE
  // hasn't rolled out yet) cannot crash the FE. All reads guard with
  // `?.` and default counts to 0 / strings/dates to null.
  dashboardSummary?: DashboardSummary
}

interface DashboardSummary {
  budget?: {
    hasBudget?: boolean
    lineItemCount?: number
    categoryCount?: number
    largestCategoryLabel?: string | null
    largestCategoryAmount?: number
  }
  lifecycle?: {
    hasLifecycle?: boolean
    totalTaskCount?: number
    completedTaskCount?: number
    inProgressTaskCount?: number
    blockedTaskCount?: number
    overdueTaskCount?: number
    currentPhase?: number
    nextMilestoneTask?: string | null
    nextMilestonePhase?: number | null
  }
  insurance?: {
    totalCount?: number
    expiringSoonCount?: number
    expiredCount?: number
    nextExpirationDate?: string | null
  }
  receipts?: {
    totalCount?: number
    totalAmount?: number
    latestReceiptDate?: string | null
  }
  bids?: {
    totalCount?: number
    receivedCount?: number
    acceptedCount?: number
    rejectedCount?: number
  }
  contracts?: {
    totalCount?: number
    draftCount?: number
    sentCount?: number
    activeCount?: number
    completedCount?: number
    unsignedCount?: number
    pendingBuilderSignatureCount?: number
  }
}

interface LifecycleTask {
  id: string
  phase: number
  name: string
  nameEs?: string
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'BLOCKED'
  sortOrder: number
  durationDays?: number
  estimatedDate?: string
  actualDate?: string
}

interface Alert {
  type: string
  severity: 'warning' | 'error' | 'info'
  message: string
  link: string
}

// ── COMPONENT ─────────────────────────────────────

export default function Dashboard() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'

  const { data: apiData, isLoading } = useQuery<DashboardData>({
    queryKey: ['dashboard', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/dashboard/${projectId}`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    refetchInterval: 60_000,
    retry: false,
  })

  const { data: apiLifecycle } = useQuery<LifecycleTask[]>({
    queryKey: ['lifecycle', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/lifecycle`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  // Fall back to demo data when backend is not running. The demo
  // fallback is intentionally narrow: it only ever flows into a real
  // user's screen when `isDemoProject(projectId)` is true (the
  // explicit `demo-*` project ids in src/utils/demoData.ts).
  const isDemo = isDemoProject(projectId)
  const data = apiData || (isDemo ? DEMO_DASHBOARD : null)
  const lifecycle = Array.isArray(apiLifecycle) ? apiLifecycle : (isDemo ? DEMO_LIFECYCLE : [])

  if (isLoading) return <DashboardSkeleton />
  // Real project with no backend data must NOT render the skeleton
  // forever — that looked like "still loading" and silently hid the
  // fact that the project simply has nothing to show yet. Render a
  // bilingual honest empty state with a clear next step instead.
  // Demo projects keep the skeleton as a defensive fallback for the
  // rare case where DEMO_DASHBOARD is somehow unavailable; in
  // practice `data` is always populated for demo projects via the
  // ternary above.
  if (!data) {
    return isDemo
      ? <DashboardSkeleton />
      : <DashboardEmpty projectId={projectId!} isEs={isEs} />
  }

  const { kpis, alerts, project } = data

  // Compute real progress from lifecycle tasks (backend kpis.progress may be stale/zero)
  const lifecycleTotal = lifecycle?.length || 0
  const lifecycleDone = lifecycle?.filter((t: any) => t?.status === 'COMPLETED').length || 0
  const computedProgress = lifecycleTotal > 0 ? Math.round((lifecycleDone / lifecycleTotal) * 100) : (kpis.progress || 0)

  // Financial Trust P0 (PR-F1): resolve the currency KPIs through the safe money
  // helper ONCE here, so the JSX can render display + trust state without ever
  // calling `.toLocaleString()` on an untrusted value.
  const totalCostMoney = formatMoney(kpis.totalCost, { isEs })
  const committedMoney = formatMoney(kpis.committed, { isEs })
  // Committed % is computed FROM committed/totalCost (ratio) so a zero/invalid
  // denominator yields no percent and a tiny denominator caps at ">999%" instead
  // of rendering a giant raw "1383525624%". (We don't trust the BE pre-computed
  // committedPct for display — it collapses zero-denominator and absurd into a
  // misleading number.)
  const committedPctR = formatPercentRatio(kpis.committed, kpis.totalCost, { isEs })
  const pendingBidsDisplay = formatCount(kpis.pendingBids)
  // $/sqft only when BOTH the total is trustworthy and a positive sqft target exists.
  const perSqftDisplay = (totalCostMoney.isValid && typeof project.sqftTarget === 'number' && project.sqftTarget > 0)
    ? formatMoneyValue(Math.round((kpis.totalCost as number) / project.sqftTarget), isEs)
    : null

  return (
    <div className="p-5 max-w-7xl mx-auto space-y-4">

      {/* ── UPLOAD HINT BANNER ─────────────────── */}
      <UploadHintBanner projectId={projectId!} />

      {/* ── A. PROJECT SUMMARY (Phase 1 Dashboard overview pass) ──
          One operational card at the top so the user immediately
          sees: which project + where in the lifecycle + what's next
          + real progress. All values are derived from real
          `project` + `lifecycle[]` — no synthesized rhythm copy
          and no client-side fabrication. */}
      <ProjectSummaryCard
        project={project}
        lifecycle={lifecycle || []}
        computedProgress={computedProgress}
      />

      {/* ── B. BLOCKERS & ATTENTION NEEDED ──
          Phase 1 promoted the previous chrome-less AlertBanner[]
          list into a titled card. Phase 2B extends the card with
          synthesized blocker rows derived from the new
          `dashboardSummary` block returned by /api/dashboard/:id
          (PR #161). Only real, already-fetched server counts are
          surfaced — no client-side blocker synthesis beyond
          formatting. Bilingual EN/ES throughout. */}
      <BlockersCard
        alerts={alerts || []}
        summary={data.dashboardSummary}
        projectId={projectId!}
      />

      {/* ── KPI CARDS — refined sub-text (Phase 1) ──
          The green/red trend arrows and rhythm copy were removed
          because they weren't tied to real signal. Sub-text now
          uses neutral, real-derived facts: $/sqft, committed %,
          current phase number, awaiting-review count. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Financial Trust P0 (PR-F1): every currency KPI goes through the safe
            money helper — an invalid/missing source value shows "Unavailable"
            + "Source data missing" instead of crashing or rendering $NaN/$∞, and
            a valid-but-suspicious value still shows but carries a subtle review
            hint. A real $0 (e.g. no budget yet) still displays as $0. */}
        <KpiCard
          label={t('Presupuesto Total')}
          value={totalCostMoney.displayValue}
          sub={!totalCostMoney.isValid
            ? moneyCopy('missing', isEs)
            : perSqftDisplay
              ? `${perSqftDisplay}/sqft`
              : (isEs ? 'Total estimado' : 'Estimated total')}
          accent
          tone={totalCostMoney.isValid ? 'normal' : 'muted'}
          note={totalCostMoney.isSuspicious ? moneyCopy('verify', isEs) : undefined}
        />
        <KpiCard
          label={t('Comprometido')}
          value={committedMoney.displayValue}
          sub={!committedMoney.isValid
            ? moneyCopy('missing', isEs)
            : committedPctR.isSuspicious
              ? moneyCopy('overBudget', isEs)
              : committedPctR.isValid
                ? `${committedPctR.displayValue} ${isEs ? 'del total' : 'of total'}`
                : moneyCopy('sourceNeedsReview', isEs)}
          tone={committedMoney.isValid ? 'normal' : 'muted'}
          note={(committedMoney.isSuspicious || committedPctR.isSuspicious) ? moneyCopy('verify', isEs) : undefined}
        />
        <KpiCard
          label={t('Avance del Proyecto')}
          value={`${computedProgress}%`}
          sub={`${isEs ? 'Fase' : 'Phase'} ${project.phase + 1} ${isEs ? 'de' : 'of'} 5`}
        />
        <KpiCard
          label={t('Bids Pendientes')}
          value={pendingBidsDisplay.displayValue}
          sub={pendingBidsDisplay.isValid && (kpis.pendingBids || 0) > 0
            ? (isEs ? 'Esperando revisión' : 'Awaiting review')
            : (isEs ? 'Sin pendientes' : 'None pending')}
        />
      </div>

      {/* PR-F3A: anomalous contract exclusion banner — shown only when at least one
          ACTIVE contract amount was excluded from the committed total. The raw amounts
          are still visible on the Contracts page; this banner explains why the
          committed KPI is lower than a user might expect from the contract list. */}
      {(kpis.committedExcludedCount ?? 0) > 0 && (
        <div
          data-testid="dashboard-committed-exclusion-banner"
          className="flex items-start gap-2 rounded-lg border border-[#F5E2B8] bg-[#FEF7E6] px-3 py-2.5 text-[11px] font-[700] text-[#9E6B00]"
        >
          <AlertTriangle size={13} className="mt-[1px] shrink-0" />
          <span>
            {isEs
              ? `Algunos montos de contratos están excluidos del costo comprometido. ${kpis.committedExcludedCount} ${kpis.committedExcludedCount === 1 ? 'monto excluido' : 'montos excluidos'} pendiente${kpis.committedExcludedCount === 1 ? '' : 's'} de revisión de fuente.`
              : `Some contract amounts are excluded from committed cost. ${kpis.committedExcludedCount} ${kpis.committedExcludedCount === 1 ? 'amount' : 'amounts'} excluded pending source review.`}
          </span>
        </div>
      )}

      {/* ── MAIN GRID ───────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Schedule snapshot — 2/3 width */}
        <div className="lg:col-span-2 space-y-4">
          <LifecycleCard lifecycle={lifecycle || []} />
          <GanttPreviewCard lifecycle={lifecycle || []} projectStartDate={project.startDate} />
        </div>

        {/* Side column — 1/3.
            Phase 1: the previous BudgetPhasesCard (hardcoded
            percentages) is replaced with the honest
            BudgetSnapshotCard which renders ONLY values the server
            already provides: totalCost, committed (+ %),
            totalSpent (when present). No synthesized phase
            breakdown. InsuranceAlertCard now passes projectId so
            its link is project-scoped (was the unscoped
            `/insurance` root). */}
        <div className="space-y-4">
          <BudgetSnapshotCard
            projectId={projectId!}
            totalCost={kpis.totalCost}
            committed={kpis.committed}
            committedPct={kpis.committedPct}
            totalSpent={kpis.totalSpent}
            // Phase 2B: real budget / receipts metadata from
            // dashboardSummary (optional — card renders sensibly
            // when the block is absent from an older BE bundle).
            budgetSummary={data.dashboardSummary?.budget}
            receiptsSummary={data.dashboardSummary?.receipts}
          />
          <QuickActionsCard projectId={projectId!} />
          {kpis.expiringInsurance > 0 && (
            <InsuranceAlertCard
              projectId={projectId!}
              // Phase 2B: pass through expiration metadata so the
              // alert body can show the real next-expiration date
              // and an explicit expired-count when present.
              insuranceSummary={data.dashboardSummary?.insurance}
            />
          )}
        </div>

      </div>
    </div>
  )
}

// ── SUB-COMPONENTS ────────────────────────────────

function KpiCard({ label, value, sub, accent, tone = 'normal', note }: {
  label: string; value: string; sub: string; accent?: boolean
  // Financial Trust P0 (PR-F1):
  //   tone='muted' → the value is unavailable/invalid (renders grey, not as a
  //     credible black total).
  //   note → a subtle bilingual review hint for a valid-but-suspicious value
  //     (e.g. unexpectedly large/negative); never blocks, never alters the value.
  tone?: 'normal' | 'muted'; note?: string
}) {
  // Phase 1 Dashboard overview pass: vanity `trend` prop removed.
  // Previously the sub-text inherited a green/red color from a
  // `trend` flag that wasn't tied to real signal (e.g., committed%
  // always rendered as "↑ up" regardless of value; progress < 50%
  // rendered as "↓ down" even on healthy early-phase projects).
  // Sub-text now renders in a single neutral grey so the KPI value
  // does the talking, not a fabricated trend arrow.
  return (
    <div className={`bg-white border rounded-xl p-3 ${accent ? 'border-l-[3px] border-l-[#06539B] border-[#ECEAE4]' : 'border-[#ECEAE4]'}`}>
      <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{label}</div>
      <div
        data-testid="kpi-value"
        className={`text-[20px] font-[900] tracking-[-0.5px] ${tone === 'muted' ? 'text-[#9E9890]' : 'text-[#1C1A17]'}`}
      >
        {value}
      </div>
      <div className="text-[9px] font-[700] mt-1 text-[#9E9890]">{sub}</div>
      {note && (
        <div data-testid="kpi-trust-note" className="text-[9px] font-[800] mt-1 text-amber-700 flex items-center gap-1">
          <AlertTriangle size={9} className="flex-shrink-0" /> {note}
        </div>
      )}
    </div>
  )
}

// PR-F1 fix: some alert messages are built SERVER-SIDE in Spanish only (e.g. the
// contractor-bids alert at src/server/index.ts), which then showed Spanish text in
// English mode. We re-localize the known structured alerts on the CLIENT by type +
// count (FE-only, no backend change). Unknown alerts fall back to the raw message.
function localizeAlertMessage(alert: Alert, isEs: boolean): string {
  if (alert.type === 'bids') {
    // Backend sends "<n> propuestas de contratistas esperando revisión"; the count
    // is always the leading integer. Rebuild a correctly-pluralized sentence.
    const n = parseInt(alert.message, 10)
    if (Number.isFinite(n)) {
      return isEs
        ? `${n} ${n === 1 ? 'propuesta de contratista' : 'propuestas de contratistas'} esperando revisión`
        : `${n} contractor ${n === 1 ? 'bid' : 'bids'} awaiting review`
    }
  }
  return alert.message
}

function AlertBanner({ alert }: { alert: Alert }) {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const colors = {
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    error:   'bg-red-50 border-red-200 text-red-800',
    info:    'bg-sky-50 border-sky-200 text-sky-800',
  }
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 rounded-full border text-[11px] font-[700] ${colors[alert.severity]}`}>
      <AlertTriangle size={14} className="flex-shrink-0" />
      <span className="flex-1" data-testid="dashboard-alert-message">{localizeAlertMessage(alert, isEs)}</span>
      {/* PR-184: React Router Link replaces bare <a> so alert clicks use
          client-side navigation instead of a full page reload. Styling,
          copy, and alert.link data source are unchanged. */}
      <Link
        to={alert.link}
        data-testid="dashboard-alert-link"
        className="font-[800] text-[10px] underline ml-auto whitespace-nowrap"
      >
        {isEs ? 'Ver →' : 'View →'}
      </Link>
    </div>
  )
}

// Phase colors matching design system
const PHASE_COLORS: Record<number, string> = {
  0: '#06B6D4',
  1: '#3B82F6',
  2: '#00BCD4',
  3: '#14B8A6',
  4: '#EC4899',
}

const STATUS_COLORS: Record<string, string> = {
  COMPLETED:   '#22C55E',
  IN_PROGRESS: '#0EA5E9',
  PENDING:     '#94A3B8',
  BLOCKED:     '#EF4444',
}

const PHASE_NAMES_EN = ['Pre-development', 'Design/Permits', 'Contracting', 'Construction', 'Closing']
const PHASE_NAMES_ES = ['Pre-desarrollo', 'Diseño/Permisos', 'Contratación', 'Construcción', 'Cierre']
function usePhaseName() { const { i18n } = useTranslation(); return (idx: number) => (i18n.language === 'es' ? PHASE_NAMES_ES : PHASE_NAMES_EN)[idx] }

function LifecycleCard({ lifecycle }: { lifecycle: LifecycleTask[] }) {
  const phaseName = usePhaseName()
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const phases = [0, 1, 2, 3, 4]

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-[#ECEAE4] flex items-center">
        <h3 className="text-[13px] font-[800] text-[#1C1A17]">{isEs ? 'Ciclo del Proyecto' : 'Project Lifecycle'}</h3>
        <span className="ml-auto text-[12px] font-[700] text-[#9E9890]">
          {lifecycle.filter(t => t.status === 'COMPLETED').length}/{lifecycle.length} {isEs ? 'tareas' : 'tasks'}
        </span>
      </div>

      {phases.map(phase => {
        const phaseTasks = lifecycle.filter(t => t.phase === phase)
        if (phaseTasks.length === 0) return null

        const done = phaseTasks.filter(t => t.status === 'COMPLETED').length
        const pct = phaseTasks.length > 0 ? Math.round((done / phaseTasks.length) * 100) : 0
        const color = PHASE_COLORS[phase]

        // Determine phase status dot
        const phaseStatus = done === phaseTasks.length ? 'COMPLETED'
          : phaseTasks.some(t => t.status === 'IN_PROGRESS') ? 'IN_PROGRESS'
          : phaseTasks.some(t => t.status === 'BLOCKED') ? 'BLOCKED'
          : 'PENDING'

        return (
          <div key={phase} className="border-t border-[#ECEAE4] first:border-t-0">
            {/* Phase header */}
            <div className="flex items-center gap-2 px-4 py-2.5 cursor-pointer hover:bg-[#F2F0EB] transition-colors">
              <span className="text-[9px] font-[900] px-2.5 py-1 rounded-full text-white" style={{ background: color }}>
                F{phase}
              </span>
              <span className="text-[12px] font-[700] flex-1">{phaseName(phase)}</span>
              <span className="text-[10px] font-[800] text-[#9E9890]">{done}/{phaseTasks.length}</span>
              {/* Status dot */}
              <div
                className="w-[8px] h-[8px] rounded-full flex-shrink-0"
                style={{ background: STATUS_COLORS[phaseStatus] }}
              />
            </div>

            {/* Phase progress bar */}
            <div className="h-[2px] mx-4 bg-[#ECEAE4] rounded-full overflow-hidden mb-1">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>

            {/* Show active + next tasks only (max 3) */}
            {phaseTasks
              .filter(t => t.status === 'IN_PROGRESS' || t.status === 'BLOCKED' || (t.status === 'PENDING' && done < phaseTasks.length))
              .slice(0, 3)
              .map(task => (
                <div key={task.id} className="flex items-center gap-2 px-4 pb-1.5 pl-10">
                  {/* Status checkbox */}
                  <div
                    className="w-[14px] h-[14px] rounded-[4px] border-[1.5px] flex-shrink-0 flex items-center justify-center"
                    style={{
                      background: task.status === 'COMPLETED' ? '#22C55E' : 'transparent',
                      borderColor: task.status === 'COMPLETED' ? '#22C55E'
                        : task.status === 'IN_PROGRESS' ? '#0EA5E9'
                        : task.status === 'BLOCKED' ? '#EF4444'
                        : '#ECEAE4'
                    }}
                  >
                    {task.status === 'COMPLETED' && (
                      <span className="text-white text-[8px] font-[900]">✓</span>
                    )}
                  </div>
                  <span className={`text-[11px] flex-1 ${task.status === 'COMPLETED' ? 'line-through text-[#9E9890]' : 'font-[700]'}`}>
                    {task.nameEs || task.name}
                  </span>
                  {/* Phase 1: bilingual status badges (were Spanish-only). */}
                  {task.status === 'BLOCKED' && (
                    <span className="text-[8px] font-[800] px-2 py-0.5 rounded-full bg-red-50 text-red-500">
                      {isEs ? 'BLOQUEADA' : 'BLOCKED'}
                    </span>
                  )}
                  {task.status === 'IN_PROGRESS' && (
                    <span className="text-[8px] font-[800] px-2 py-0.5 rounded-full bg-sky-50 text-sky-500">
                      {isEs ? 'EN CURSO' : 'IN PROGRESS'}
                    </span>
                  )}
                </div>
              ))}
          </div>
        )
      })}
    </div>
  )
}

function GanttPreviewCard({ lifecycle, projectStartDate }: { lifecycle: LifecycleTask[]; projectStartDate?: string }) {
  const phaseName = usePhaseName()
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const phases = [0, 1, 2, 3, 4]
  const phaseData = phases.map(p => {
    const tasks = lifecycle.filter(t => t.phase === p)
    const totalDays = tasks.reduce((sum, t) => sum + (t.durationDays || 7), 0)
    return { phase: p, tasks, done: tasks.filter(t => t.status === 'COMPLETED').length, totalDays }
  }).filter(p => p.tasks.length > 0)

  // Calculate dynamic timeline from project start date
  const startDate = projectStartDate ? new Date(projectStartDate) : new Date()
  const totalProjectDays = phaseData.reduce((sum, p) => sum + p.totalDays, 0)
  const endDate = new Date(startDate.getTime() + totalProjectDays * 24 * 60 * 60 * 1000)

  // Generate month labels dynamically
  const months: string[] = []
  const MONTH_LABELS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC']
  const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1)
  while (cursor <= endDate) {
    months.push(MONTH_LABELS[cursor.getMonth()])
    cursor.setMonth(cursor.getMonth() + 1)
  }

  // Calculate today position as percentage of total timeline
  const now = new Date()
  const totalMs = endDate.getTime() - startDate.getTime()
  const todayMs = now.getTime() - startDate.getTime()
  const todayLeft = totalMs > 0 ? Math.max(0, Math.min(100, (todayMs / totalMs) * 100)) : 0

  // Calculate cumulative phase offsets
  let cumulativeDays = 0
  const phasePositions = phaseData.map(p => {
    const leftPct = totalProjectDays > 0 ? (cumulativeDays / totalProjectDays) * 100 : 0
    const widthPct = totalProjectDays > 0 ? (p.totalDays / totalProjectDays) * 100 : 10
    cumulativeDays += p.totalDays
    return { ...p, leftPct, widthPct }
  })

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-[#ECEAE4] flex items-center">
        <h3 className="text-[13px] font-[800]">{isEs ? 'Gantt — Cronograma' : 'Gantt — Schedule'}</h3>
        <span className="ml-auto text-[12px] font-[700] text-[#9E9890]">
          {totalProjectDays} {isEs ? 'días estimados' : 'estimated days'}
        </span>
      </div>
      <div className="p-4">
        {/* Month headers — F6 from Responsive UX Parity Audit:
            below `sm`, narrow the gutter so the timeline gets more
            horizontal space (was hard-coded `ml-[90px]` which left
            only ~270px for the bars on a 360px viewport). */}
        <div className="flex ml-[64px] sm:ml-[90px] mb-1">
          {months.map((m, i) => (
            <div key={`${m}-${i}`} className="flex-1 text-center text-[9px] sm:text-[8px] font-[800] text-[#9E9890] tracking-[0.5px]">{m}</div>
          ))}
        </div>

        {/* Phase bars — pill style with dependency locks */}
        {phasePositions.map(({ phase, done, tasks, leftPct, widthPct }, idx) => {
          const color = PHASE_COLORS[phase]
          const pct = tasks.length > 0 ? done / tasks.length : 0
          const isComplete = pct === 1
          const isActive = pct > 0 && !isComplete
          const opacity = isComplete ? 0.65 : isActive ? 1 : 0.3

          // Check if previous phase is complete (dependency)
          const prevPhase = idx > 0 ? phasePositions[idx - 1] : null
          const prevComplete = prevPhase ? (prevPhase.done === prevPhase.tasks.length) : true
          const isLocked = !prevComplete && !isActive && !isComplete

          return (
            <div key={phase}>
              {/* Dependency arrow between phases — F6: gutter
                  matches the phase-row gutter (narrower on mobile). */}
              {idx > 0 && (
                <div className="flex items-center h-[14px] ml-[64px] sm:ml-[90px]">
                  <div className="relative flex-1 flex items-center" style={{ paddingLeft: `${phasePositions[idx - 1].leftPct + phasePositions[idx - 1].widthPct}%` }}>
                    <div className="flex items-center gap-0.5">
                      <div className="w-[12px] h-[1.5px] bg-[#ECEAE4] rounded" />
                      {isLocked ? (
                        <Lock size={8} className="text-[#EF4444] flex-shrink-0" />
                      ) : prevComplete ? (
                        <ArrowRight size={8} className="text-[#22C55E] flex-shrink-0" />
                      ) : (
                        <ArrowRight size={8} className="text-[#ECEAE4] flex-shrink-0" />
                      )}
                      <div className="w-[12px] h-[1.5px] bg-[#ECEAE4] rounded" />
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-center h-[28px] mb-0.5">
                {/* F6: narrower gutter on mobile (64px) + slightly
                    bigger label text (10px) so phase names stay
                    readable; desktop unchanged. */}
                <div className="w-[64px] sm:w-[90px] flex items-center gap-1 pr-2">
                  {isLocked && <Lock size={9} className="text-[#EF4444] flex-shrink-0" />}
                  <span className={`text-[10px] sm:text-[9px] font-[700] whitespace-nowrap overflow-hidden text-ellipsis ${isLocked ? 'text-[#EF4444]' : 'text-[#9E9890]'}`}>
                    {phaseName(phase)}
                  </span>
                </div>
                <div className="flex-1 relative h-full flex items-center">
                  {/* Today line */}
                  <div
                    className="absolute top-0 bottom-0 w-[2px] bg-red-400 opacity-30 z-10 rounded-full"
                    style={{ left: `${todayLeft}%` }}
                  />
                  {/* Phase bar — PILL */}
                  <div
                    className={`absolute h-[18px] rounded-full flex items-center px-2 text-[8px] font-[800] text-white/90 whitespace-nowrap overflow-hidden ${isLocked ? 'border border-dashed border-[#EF4444]/40' : ''}`}
                    style={{
                      left: `${leftPct}%`,
                      width: `${Math.max(widthPct, 5)}%`,
                      background: isLocked ? '#F2F0EB' : color,
                      opacity: isLocked ? 1 : opacity,
                      color: isLocked ? '#EF4444' : undefined,
                    }}
                  >
                    {isLocked && <Lock size={8} className="mr-1 flex-shrink-0" />}
                    {phaseName(phase).split('/')[0]}
                  </div>
                  {/* Status dot at end */}
                  <div
                    className="absolute w-[8px] h-[8px] rounded-full border-[1.5px] border-white z-10"
                    style={{
                      left: `${leftPct + widthPct}%`,
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: isComplete ? '#22C55E' : isActive ? '#0EA5E9' : isLocked ? '#EF4444' : '#94A3B8'
                    }}
                  />
                </div>
              </div>
            </div>
          )
        })}

        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#ECEAE4] flex-wrap">
          {Object.entries(PHASE_COLORS).map(([p, c]) => (
            <div key={p} className="flex items-center gap-1.5 text-[8px] text-[#9E9890] font-[700]">
              <div className="h-[8px] w-[16px] rounded-full" style={{ background: c }} />
              {phaseName(+p).split('/')[0]}
            </div>
          ))}
          <div className="flex items-center gap-1.5 text-[8px] text-[#9E9890] font-[700]">
            <div className="w-[2px] h-[12px] bg-red-400 opacity-60 rounded" />
            {isEs ? 'Hoy' : 'Today'}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── A. ProjectSummaryCard (Phase 1 Dashboard overview pass) ──────
//
// One operational summary card at the top of the dashboard. Renders
// ONLY real values from `project` and `lifecycle[]`:
//   • project name (+ address if available)
//   • current phase chip (project.phase)
//   • next milestone (first non-COMPLETED task in current phase,
//     fallback to first non-COMPLETED in any later phase)
//   • progress (computed from lifecycle done / total — same value
//     the KPI grid uses)
//
// No synthesized copy, no health rating, no model-derived score. If
// a value is missing it surfaces an honest empty placeholder, not a
// fabricated one.
function ProjectSummaryCard({
  project,
  lifecycle,
  computedProgress,
}: {
  project: Project
  lifecycle: LifecycleTask[]
  computedProgress: number
}) {
  const phaseName = usePhaseName()
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'

  // Next milestone: first IN_PROGRESS, then first PENDING, then first
  // BLOCKED — searched within the current phase first, then forward
  // through later phases. Already-COMPLETED tasks are skipped.
  // Returns null when nothing is pending (project is fully closed).
  const pickNextMilestone = (): LifecycleTask | null => {
    const isOpen = (t: LifecycleTask) => t.status !== 'COMPLETED'
    const ordered = [...lifecycle].sort((a, b) =>
      a.phase !== b.phase ? a.phase - b.phase : a.sortOrder - b.sortOrder,
    )
    // Prefer current-phase first, then any later phase.
    const currentPhase = ordered.filter(t => t.phase === project.phase && isOpen(t))
    if (currentPhase.length > 0) return currentPhase[0]
    const futurePhase = ordered.filter(t => t.phase > project.phase && isOpen(t))
    return futurePhase[0] ?? null
  }

  const next = pickNextMilestone()
  const phaseColor = PHASE_COLORS[project.phase] ?? '#9E9890'
  const locationLine = [project.address, project.city, project.state]
    .filter(Boolean)
    .join(', ')

  return (
    <div
      className="bg-white border border-[#ECEAE4] rounded-xl p-4"
      data-testid="dashboard-project-summary"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#06539B]/10 flex items-center justify-center flex-shrink-0">
          <Briefcase size={16} className="text-[#06539B]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
            {isEs ? 'Resumen del Proyecto' : 'Project Summary'}
          </div>
          <h2 className="text-[16px] font-[900] tracking-[-0.3px] text-[#1C1A17] mt-0.5 truncate">
            {project.name}
          </h2>
          {locationLine && (
            <div className="text-[11px] font-[600] text-[#9E9890] mt-0.5 truncate">
              {locationLine}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Current phase */}
        <div className="bg-[#FAFAF8] rounded-lg p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
            {isEs ? 'Fase Actual' : 'Current Phase'}
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <span
              className="text-[8px] font-[900] px-2 py-0.5 rounded-full text-white"
              style={{ background: phaseColor }}
            >
              F{project.phase}
            </span>
            <span className="text-[12px] font-[800] text-[#1C1A17] truncate">
              {phaseName(project.phase)}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-[#FAFAF8] rounded-lg p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
            {isEs ? 'Avance' : 'Progress'}
          </div>
          <div className="text-[16px] font-[900] tracking-[-0.5px] text-[#1C1A17] mt-0.5">
            {computedProgress}%
          </div>
          <div className="h-[3px] bg-[#ECEAE4] rounded-full overflow-hidden mt-1">
            <div
              className="h-full rounded-full"
              style={{ width: `${computedProgress}%`, background: phaseColor }}
            />
          </div>
        </div>

        {/* Next milestone */}
        <div className="bg-[#FAFAF8] rounded-lg p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
            {isEs ? 'Próximo Hito' : 'Next Milestone'}
          </div>
          {next ? (
            <div className="text-[12px] font-[700] text-[#1C1A17] mt-0.5 truncate" title={next.nameEs || next.name}>
              {isEs ? (next.nameEs || next.name) : next.name}
            </div>
          ) : (
            <div className="text-[12px] font-[700] text-[#9E9890] mt-0.5">
              {isEs ? 'Sin tareas pendientes' : 'No pending tasks'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── B. BlockersCard (Phase 1 + Phase 2B Dashboard overview pass) ──
//
// Phase 1: promoted the previous chrome-less list of AlertBanner
// rows into a titled card with severity ordering + an honest empty
// state. Uses `data.alerts[]` from `/api/dashboard/:id`.
//
// Phase 2B: synthesizes ADDITIONAL bilingual blocker rows from the
// new `dashboardSummary` block returned by PR #161. All synthesized
// rows are gated on `count > 0` against the REAL server counts —
// no client-side blocker invention. Severity classification per
// the Phase 2B spec:
//   • lifecycle.blockedTaskCount > 0          → error
//   • lifecycle.overdueTaskCount > 0          → warning
//   • contracts.unsignedCount > 0             → warning
//   • contracts.pendingBuilderSignatureCount > 0 → warning
//   • insurance.expiredCount > 0              → error
//
// Synthesized rows are merged with `alerts[]` and sorted by
// severity (error → warning → info). Duplicates against the existing
// alerts[] are detected by the row's `link` URL so the same blocker
// never appears twice (e.g. if a future BE pushes an "expired
// insurance" alert AND dashboardSummary.insurance.expiredCount > 0).
function BlockersCard({
  alerts,
  summary,
  projectId,
}: {
  alerts: Alert[]
  summary: DashboardSummary | undefined
  projectId: string
}) {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'

  // Build synthesized rows from real dashboardSummary counts only.
  // Each row keeps the same `Alert` shape so the existing
  // AlertBanner renderer can consume it without changes.
  const synthesized: Alert[] = []

  const blockedTasks = summary?.lifecycle?.blockedTaskCount ?? 0
  if (blockedTasks > 0) {
    synthesized.push({
      type: 'lifecycle-blocked',
      severity: 'error',
      message: isEs
        ? `${blockedTasks} tarea(s) bloqueada(s)`
        : `${blockedTasks} blocked task(s)`,
      link: `/projects/${projectId}/gantt`,
    })
  }

  const overdueTasks = summary?.lifecycle?.overdueTaskCount ?? 0
  if (overdueTasks > 0) {
    synthesized.push({
      type: 'lifecycle-overdue',
      severity: 'warning',
      message: isEs
        ? `${overdueTasks} tarea(s) atrasada(s)`
        : `${overdueTasks} overdue task(s)`,
      link: `/projects/${projectId}/gantt`,
    })
  }

  const unsigned = summary?.contracts?.unsignedCount ?? 0
  if (unsigned > 0) {
    synthesized.push({
      type: 'contracts-unsigned',
      severity: 'warning',
      message: isEs
        ? `${unsigned} contrato(s) sin firmar`
        : `${unsigned} contract(s) awaiting signature`,
      link: `/projects/${projectId}/contracts`,
    })
  }

  const pendingCountersign = summary?.contracts?.pendingBuilderSignatureCount ?? 0
  if (pendingCountersign > 0) {
    synthesized.push({
      type: 'contracts-pending-countersign',
      severity: 'warning',
      message: isEs
        ? `${pendingCountersign} contrato(s) esperando tu firma`
        : `${pendingCountersign} contract(s) awaiting your countersign`,
      link: `/projects/${projectId}/contracts`,
    })
  }

  const expiredInsurance = summary?.insurance?.expiredCount ?? 0
  if (expiredInsurance > 0) {
    synthesized.push({
      type: 'insurance-expired',
      severity: 'error',
      message: isEs
        ? `${expiredInsurance} póliza(s) vencida(s)`
        : `${expiredInsurance} expired insurance policy/policies`,
      link: `/projects/${projectId}/insurance`,
    })
  }

  // De-dupe against existing alerts by `link`. If a future BE push
  // already covers the same destination, drop the synthesized copy
  // — the BE message wins.
  const existingLinks = new Set(alerts.map(a => a.link))
  const deduped = synthesized.filter(s => !existingLinks.has(s.link))

  const merged: Alert[] = [...alerts, ...deduped]
  const sorted = [...merged].sort((a, b) => {
    const rank: Record<Alert['severity'], number> = { error: 0, warning: 1, info: 2 }
    return rank[a.severity] - rank[b.severity]
  })

  return (
    <div
      className="bg-white border border-[#ECEAE4] rounded-xl p-4"
      data-testid="dashboard-blockers"
    >
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={14} className="text-[#9E9890]" />
        <h3 className="text-[12px] font-[800] text-[#1C1A17]">
          {isEs ? 'Atención Necesaria' : 'Attention Needed'}
        </h3>
        <span className="ml-auto text-[10px] font-[700] text-[#9E9890]">
          {sorted.length}
        </span>
      </div>
      {sorted.length === 0 ? (
        <div className="text-[11px] font-[700] text-[#9E9890] text-center py-2" data-testid="dashboard-blockers-empty">
          {isEs ? 'Todo al día. Sin pendientes.' : 'All caught up. Nothing pending.'}
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map((alert, i) => (
            <AlertBanner key={`${alert.type}-${i}`} alert={alert} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── C. BudgetSnapshotCard (Phase 1 + Phase 2B overview pass) ─────
//
// Phase 1: replaced the previous BudgetPhasesCard (hardcoded
// 17/6/10/43/18/6% phase breakdown) with a card surfacing ONLY
// real values from /api/dashboard/:id (totalCost, committed +
// pct, totalSpent).
//
// Phase 2B: defensively consumes the new `dashboardSummary.budget`
// + `dashboardSummary.receipts` blocks. Adds:
//   • a calm "No budget yet" empty state when the project has no
//     budget rows (hasBudget === false OR lineItemCount === 0) —
//     not styled as a blocker, with a CTA to Budget.
//   • a small "Largest: {category} — ${amount}" line under the
//     totals when largestCategoryLabel + largestCategoryAmount > 0.
//   • an optional "{n} receipts recorded" line when receipts
//     totalCount > 0. (Per spec: do not surface per-row review-
//     state counts — Receipt model has no review-state column.)
// Every consumption reads via `?.` with safe defaults so an
// older BE response (no dashboardSummary) renders the Phase 1
// card unchanged.
function BudgetSnapshotCard({
  projectId,
  totalCost,
  committed,
  committedPct,
  totalSpent,
  budgetSummary,
  receiptsSummary,
}: {
  projectId: string
  totalCost: number
  committed: number
  committedPct: number
  totalSpent?: number
  budgetSummary?: DashboardSummary['budget']
  receiptsSummary?: DashboardSummary['receipts']
}) {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  // PR-F1: trust-guard the headline total + percentages once for this card.
  const totalCostBudget = formatMoney(totalCost, { isEs })
  // Percentages are computed via the ratio helper: zero/invalid denominator → "—",
  // absurd (tiny denominator) → capped ">999%" + suspicious — never a giant raw %.
  const committedPctR = formatPercentRatio(committed, totalCost, { isEs })
  const spentPctR = formatPercentRatio(totalSpent, totalCost, { isEs })
  const showSpent = typeof totalSpent === 'number' && Number.isFinite(totalSpent) && totalSpent > 0
  // Keep the review copy visible whenever a percentage had to be capped.
  const showPctReview = committedPctR.isSuspicious || spentPctR.isSuspicious

  // Phase 2B: no-budget empty state. Only triggers when the BE has
  // explicitly told us hasBudget is false OR lineItemCount is 0.
  // We deliberately do NOT infer "no budget" from totalCost === 0
  // alone — older BE responses (no dashboardSummary) fall through
  // to the Phase 1 card with $0 / committed / etc.
  const hasBudgetSignal = typeof budgetSummary?.hasBudget === 'boolean'
    || typeof budgetSummary?.lineItemCount === 'number'
  const noBudget = hasBudgetSignal && (
    budgetSummary?.hasBudget === false ||
    (budgetSummary?.lineItemCount ?? 0) === 0
  )

  if (noBudget) {
    return (
      <div
        className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3"
        data-testid="dashboard-budget-snapshot"
      >
        <div className="flex items-center gap-2">
          <Wallet size={14} className="text-[#9E9890]" />
          <h3 className="text-[12px] font-[800]">
            {isEs ? 'Resumen del Presupuesto' : 'Budget Snapshot'}
          </h3>
        </div>
        <div
          className="text-center py-2"
          data-testid="dashboard-budget-snapshot-empty"
        >
          <div className="text-[13px] font-[800] text-[#1C1A17]">
            {isEs ? 'Sin presupuesto definido' : 'No budget yet'}
          </div>
          <div className="text-[10px] font-[600] text-[#9E9890] mt-1 leading-relaxed">
            {isEs
              ? 'Agrega partidas para empezar a ver presupuesto, comprometido y gastado.'
              : 'Add budget items to start seeing budget, committed, and spent totals.'}
          </div>
        </div>
        {/* PR-186: <a href> → <Link to> so clicking uses client-side
            navigation instead of a full page reload. Destination,
            styling, and copy are unchanged. */}
        <Link
          to={`/projects/${projectId}/budget`}
          className="block text-center text-[11px] font-[800] text-[#06539B] hover:bg-[#06539B]/5 rounded-full border border-[#06539B]/30 py-2 transition-colors"
          data-testid="dashboard-budget-snapshot-link"
        >
          {isEs ? 'Agregar partidas →' : 'Add budget items →'}
        </Link>
      </div>
    )
  }

  // Phase 2B: optional largest-category line below the totals.
  // Only renders when both label AND positive amount are present.
  const largestLabel = budgetSummary?.largestCategoryLabel ?? null
  const largestAmount = budgetSummary?.largestCategoryAmount ?? 0
  const showLargest = !!largestLabel && largestAmount > 0

  // Phase 2B: optional receipts-recorded line. Only when > 0.
  // No reviewStatus counts — schema doesn't support them.
  const receiptCount = receiptsSummary?.totalCount ?? 0
  const showReceiptLine = receiptCount > 0

  return (
    <div
      className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden"
      data-testid="dashboard-budget-snapshot"
    >
      <div className="px-4 py-3 border-b border-[#ECEAE4] flex items-center gap-2">
        <Wallet size={14} className="text-[#9E9890]" />
        <h3 className="text-[12px] font-[800]">
          {isEs ? 'Resumen del Presupuesto' : 'Budget Snapshot'}
        </h3>
      </div>
      <div className="px-4 py-3 border-b border-[#ECEAE4]">
        {/* PR-F1: safe money — invalid/missing total shows "Unavailable" (muted), never $NaN. */}
        <div className={`text-[18px] font-[900] tracking-[-1px] ${totalCostBudget.isValid ? 'text-[#1C1A17]' : 'text-[#9E9890]'}`}>
          {totalCostBudget.displayValue}
        </div>
        <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">
          {totalCostBudget.isValid
            ? (isEs ? 'Presupuesto total' : 'Total budget')
            : moneyCopy('missing', isEs)}
        </div>
      </div>
      <div className="py-1">
        <div className="flex items-center gap-2 px-4 py-1.5">
          <div className="text-[10px] font-[700] flex-1 text-[#1C1A17]">
            {isEs ? 'Comprometido' : 'Committed'}
          </div>
          <div className="text-[10px] font-[800] text-[#1C1A17]">
            {formatMoneyValue(committed, isEs)}
          </div>
          <div data-testid="dashboard-budget-committed-pct" className="text-[9px] font-[700] text-[#9E9890] w-[52px] text-right">
            {committedPctR.displayValue}
          </div>
        </div>
        {showSpent && (
          <div className="flex items-center gap-2 px-4 py-1.5">
            <div className="text-[10px] font-[700] flex-1 text-[#1C1A17]">
              {isEs ? 'Gastado' : 'Spent'}
            </div>
            <div className="text-[10px] font-[800] text-[#1C1A17]">
              {formatMoneyValue(totalSpent, isEs)}
            </div>
            <div data-testid="dashboard-budget-spent-pct" className="text-[9px] font-[700] text-[#9E9890] w-[52px] text-right">
              {spentPctR.displayValue}
            </div>
          </div>
        )}
        {/* Phase 2B: largest-category line. Renders ONLY when both
            label and a positive amount are present in the server
            response — never a fabricated "Largest: Other" fallback. */}
        {showLargest && (
          <div
            className="flex items-center gap-2 px-4 py-1.5 border-t border-[#F4F2EE]"
            data-testid="dashboard-budget-snapshot-largest-category"
          >
            <div className="text-[10px] font-[700] flex-1 text-[#9E9890]">
              {isEs ? `Mayor: ${largestLabel}` : `Largest: ${largestLabel}`}
            </div>
            <div className="text-[10px] font-[800] text-[#1C1A17]">
              {formatMoneyValue(largestAmount, isEs)}
            </div>
          </div>
        )}
        {/* Phase 2B: receipts-recorded line. Per spec: do NOT add
            pendingReview / approved / rejected counts — Receipt
            schema has no per-row review-state column (Phase 3). */}
        {showReceiptLine && (
          <div
            className="flex items-center gap-2 px-4 py-1.5 border-t border-[#F4F2EE]"
            data-testid="dashboard-budget-snapshot-receipts"
          >
            <div className="text-[10px] font-[700] flex-1 text-[#9E9890]">
              {isEs
                ? `${receiptCount} recibo(s) registrados`
                : `${receiptCount} receipts recorded`}
            </div>
          </div>
        )}
        {/* PR-F1: when a percentage had to be capped (absurd ratio from a tiny/zero
            budget vs large committed/spent), keep the real dollar values AND show a
            visible review hint so the capped "%" isn't mistaken for a glitch. */}
        {showPctReview && (
          <div
            className="flex items-center gap-1 px-4 py-1.5 border-t border-[#F4F2EE] text-[9px] font-[800] text-amber-700"
            data-testid="dashboard-budget-snapshot-review"
          >
            <AlertTriangle size={9} className="flex-shrink-0" /> {moneyCopy('verify', isEs)}
          </div>
        )}
      </div>
      {/* PR-186: <a href> → <Link to> so clicking uses client-side
          navigation instead of a full page reload. Destination,
          styling, and copy are unchanged. */}
      <Link
        to={`/projects/${projectId}/budget`}
        className="block px-4 py-2.5 text-center text-[11px] font-[800] text-[#06539B] hover:bg-[#06539B]/5 border-t border-[#ECEAE4] transition-colors"
        data-testid="dashboard-budget-snapshot-link"
      >
        {isEs ? 'Ver Presupuesto →' : 'See Budget →'}
      </Link>
    </div>
  )
}

function QuickActionsCard({ projectId }: { projectId: string }) {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  // Cuantificación switch (Blu 2026-10-21): when Cuantificación is off for
  // this user, "Subir plano" goes to Marcación de Planos instead.
  const takeoffAvailable = useTakeoffAvailable()
  // Phase 1: emoji icons (plan upload / quote / hammer / signature)
  // replaced with lucide-react icons (Upload / FileText / Hammer /
  // ScrollText) for consistency with the rest of the app. Same 4
  // actions, same 4 href targets — no new routes, no new actions.
  const actions = [
    { Icon: Upload,     label: isEs ? 'Subir plano' : 'Upload Plan',       href: takeoffAvailable ? `/projects/${projectId}/takeoff` : `/projects/${projectId}/markup`, color: '#06B6D4' },
    { Icon: FileText,   label: isEs ? 'Pedir cotización' : 'Request Quote', href: `/projects/${projectId}/rfq`,           color: '#00BCD4' },
    { Icon: Hammer,     label: isEs ? 'Ver propuestas' : 'View Bids',      href: `/projects/${projectId}/bids`,          color: '#14B8A6' },
    { Icon: ScrollText, label: isEs ? 'Nuevo contrato' : 'New Contract',   href: `/projects/${projectId}/contracts/new`, color: '#EC4899' },
  ]
  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden" data-testid="dashboard-quick-actions">
      <div className="px-4 py-3 border-b border-[#ECEAE4]">
        <h3 className="text-[12px] font-[800]">{isEs ? 'Acciones Rápidas' : 'Quick Actions'}</h3>
      </div>
      <div className="p-3 grid grid-cols-2 gap-2">
        {/* PR-186: <a href> → <Link to> so each quick action uses
            client-side navigation. Array, destinations, icons,
            labels, and styling are unchanged. */}
        {actions.map(a => (
          <Link
            key={a.label}
            to={a.href}
            className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-[#ECEAE4] hover:border-[#06539B] transition-colors text-center"
          >
            <a.Icon size={18} style={{ color: a.color }} />
            <span className="text-[10px] font-[700] text-[#1C1A17]">{a.label}</span>
          </Link>
        ))}
      </div>
    </div>
  )
}

function InsuranceAlertCard({
  projectId,
  insuranceSummary,
}: {
  projectId: string
  insuranceSummary?: DashboardSummary['insurance']
}) {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'

  // Phase 2B: enhance the alert body with REAL data from
  // dashboardSummary.insurance when present. Two safe surfaces:
  //   • expiredCount > 0 → escalate from "expiring soon" framing to
  //     an explicit "X expired" count (stronger signal).
  //   • nextExpirationDate present → show the bilingual next-
  //     expiration line so the user knows when the next renewal is
  //     actually due.
  // Both surfaces are read defensively via `?.` and don't change
  // the card's existing color/severity styling. Per spec: do NOT
  // surface required-vs-optional insurance counts — schema has no
  // required-type concept (Phase 3).
  const expiredCount = insuranceSummary?.expiredCount ?? 0
  const showExpiredEscalation = expiredCount > 0
  const nextDateIso = insuranceSummary?.nextExpirationDate ?? null
  const nextDateFormatted = nextDateIso
    ? new Date(nextDateIso).toLocaleDateString(isEs ? 'es-MX' : 'en-US', {
        day: 'numeric', month: 'short', year: 'numeric',
      })
    : null

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="dashboard-insurance-alert">
      <div className="flex items-center gap-2 mb-2">
        <Shield size={16} className="text-amber-600" />
        <h3 className="text-[12px] font-[800] text-amber-800">{isEs ? 'Seguros por Vencer' : 'Expiring Insurance'}</h3>
      </div>
      {/* Phase 2B: when the BE reports any policy strictly past its
          endDate, surface that count explicitly above the generic
          "expiring soon" copy. */}
      {showExpiredEscalation && (
        <p
          className="text-[11px] text-amber-800 font-[800] mb-1"
          data-testid="dashboard-insurance-alert-expired"
        >
          {isEs
            ? `${expiredCount} póliza(s) vencida(s)`
            : `${expiredCount} expired insurance policy/policies`}
        </p>
      )}
      <p className="text-[11px] text-amber-700 font-[600] mb-2">
        {isEs ? 'Uno o más seguros vencen pronto. Renueva antes de continuar la construcción.' : 'One or more policies expiring soon. Renew before continuing construction.'}
      </p>
      {/* Phase 2B: surface the next real expiration date when the
          BE provides it. Always renders below the body text, calm
          styling — no fabricated dates. */}
      {nextDateFormatted && (
        <p
          className="text-[10px] text-amber-700 font-[700] mb-3"
          data-testid="dashboard-insurance-alert-next-date"
        >
          {isEs
            ? `Próximo vencimiento: ${nextDateFormatted}`
            : `Next expiration: ${nextDateFormatted}`}
        </p>
      )}
      {/* Phase 1: project-scoped link (was bare `/insurance` which
          navigated to the no-project-context root).
          PR-186: <a href> → <Link to> for client-side navigation.
          Destination, styling, and copy are unchanged. */}
      <Link
        to={`/projects/${projectId}/insurance`}
        className="block text-center text-[13px] font-[800] bg-amber-600 text-white rounded-full py-2 px-4 hover:bg-amber-700 transition-colors"
        data-testid="dashboard-insurance-alert-link"
      >
        {isEs ? 'Ver Seguros →' : 'View Insurance →'}
      </Link>
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="p-5 space-y-4 animate-pulse">
      <div className="grid grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-[#ECEAE4] rounded-xl h-20" />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 bg-[#ECEAE4] rounded-xl h-64" />
        <div className="bg-[#ECEAE4] rounded-xl h-64" />
      </div>
    </div>
  )
}

/**
 * Honest empty state for the Dashboard when a REAL (non-demo)
 * project has no backend data yet.
 *
 * Why this exists:
 *   Before this PR the Dashboard rendered `<DashboardSkeleton />`
 *   forever when `apiData` was null/undefined and `isDemoProject`
 *   was false. The skeleton is a *loading* affordance — pulsing
 *   gray blocks — which silently disguised "this project has no
 *   data yet" as "still loading." That is exactly the kind of
 *   fake-looking surface the product rule "no fake KPIs, no
 *   demo-as-real" exists to prevent.
 *
 * What this renders:
 *   A bilingual EN/ES headline, a supporting sentence pointing
 *   at the modules a builder typically starts with, and a single
 *   primary CTA into Budget (the natural first-step module after
 *   project creation). No KPI numbers, no zero-styled-as-real
 *   values, no demo content.
 *
 * Out of scope:
 *   This component never reads DEMO_DASHBOARD or DEMO_LIFECYCLE.
 *   Demo projects keep their existing behavior (skeleton while
 *   loading, then demo data via the gated ternary in Dashboard).
 */
function DashboardEmpty({ projectId, isEs }: { projectId: string; isEs: boolean }) {
  const navigate = useNavigate()
  return (
    <div className="p-5 max-w-3xl mx-auto" data-testid="dashboard-empty">
      <div className="bg-white border border-[#ECEAE4] rounded-2xl p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-[#06539B]/10 flex items-center justify-center mx-auto mb-4">
          <TrendingUp size={22} className="text-[#06539B]" />
        </div>
        <h2 className="text-[18px] font-[900] tracking-[-0.3px] text-[#1C1A17]">
          {isEs ? 'Aún no hay datos del proyecto.' : 'No project data yet.'}
        </h2>
        <p className="text-[12px] font-[600] text-[#6B6660] mt-2 max-w-md mx-auto leading-relaxed">
          {isEs
            ? 'Agrega presupuesto, calendario, documentos o actividad para empezar a ver métricas reales del proyecto.'
            : 'Add budget, schedule, documents, or activity to start seeing live project metrics.'}
        </p>
        <button
          type="button"
          onClick={() => navigate(`/projects/${projectId}/budget`)}
          className="mt-5 inline-flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-5 py-2.5 rounded-full hover:bg-[#14457E] transition-colors"
          aria-label={isEs ? 'Empezar con Presupuesto' : 'Start with Budget'}
        >
          {isEs ? 'Empezar con Presupuesto' : 'Start with Budget'}
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  )
}

// ── UPLOAD HINT BANNER ────────────────────────────

function UploadHintBanner({ projectId }: { projectId: string }) {
  const takeoffAvailable = useTakeoffAvailable()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const storageKey = `bluaiq-hint-upload-${projectId}`
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1' } catch { return false }
  })

  if (dismissed) return null

  const handleDismiss = () => {
    try { localStorage.setItem(storageKey, '1') } catch {}
    setDismissed(true)
  }

  return (
    <div className="bg-[#FFF9F0] border border-[#F5A623]/30 rounded-xl p-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-[#F5A623]/15 flex items-center justify-center flex-shrink-0">
        <Upload size={18} className="text-[#F5A623]" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[13px] font-[900] text-[#1C1A17]">{t('onboarding.uploadHint.title')}</h3>
        <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">{t(takeoffAvailable ? 'onboarding.uploadHint.body' : 'onboarding.uploadHint.bodyMarkupOnly')}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {takeoffAvailable && (
            <button
              onClick={() => navigate(`/projects/${projectId}/takeoff`)}
              className="bg-[#06539B] hover:bg-[#14457E] text-white rounded-full px-4 py-1.5 text-[10px] font-[800] transition-colors"
            >
              {t('onboarding.uploadHint.ctaTakeoff')}
            </button>
          )}
          <button
            onClick={() => navigate(`/projects/${projectId}/drawings`)}
            className="border border-[#ECEAE4] hover:bg-[#F2F0EB] rounded-full px-4 py-1.5 text-[10px] font-[800] text-[#1C1A17] transition-colors"
          >
            {t('onboarding.uploadHint.ctaDrawings')}
          </button>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        aria-label={t('onboarding.uploadHint.dismiss')}
        className="flex-shrink-0 w-7 h-7 rounded-full hover:bg-[#F2F0EB] flex items-center justify-center text-[#9E9890] transition-colors"
      >
        <X size={14} />
      </button>
    </div>
  )
}
